'use client'
import { useState } from 'react'
import { StickyNote, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { noteApi } from '@/services/note.service'

interface QuickNoteFabProps {
  patientId: string
}

export function QuickNoteFab({ patientId }: QuickNoteFabProps) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [isPending, setIsPending] = useState(false)
  const { toast } = useToast()

  async function handleSave() {
    if (!text.trim()) return
    setIsPending(true)
    try {
      // NOM-004 REQ-VIS-05: this is an informal, NON-clinical annotation — it is
      // never a progress note and never appears in the evolution timeline.
      await noteApi.createNote({
        patient_id: patientId,
        title: `Anotación — ${new Date().toLocaleDateString('es-MX')}`,
        content: text.trim(),
        note_type: 'OTHER',
      })
      toast({ title: 'Anotación guardada', description: 'La anotación (no clínica) fue registrada.' })
      setText('')
      setOpen(false)
      window.dispatchEvent(new Event('patient-notes-updated'))
    } catch {
      toast({ title: 'Error', description: 'No se pudo guardar la anotación.', variant: 'destructive' })
    } finally {
      setIsPending(false)
    }
  }

  return (
    <div className="fixed bottom-6 right-6 z-20">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            className="flex items-center gap-2 bg-ml-secondary text-white rounded px-5 py-3 shadow-lg shadow-black/10 hover:opacity-90 transition-opacity bg-slate-600"
            aria-label="Agregar anotación no clínica"
          >
            <StickyNote className="h-5 w-5" aria-hidden="true" />
            <span className="text-sm font-bold">Anotación</span>
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader className="pr-6">
            <DialogTitle>Anotación (no clínica)</DialogTitle>
            <DialogDescription>
              Apunte de trabajo informal. <strong>No</strong> forma parte del expediente clínico
              ni de la evolución; para registrar información clínica inicia una consulta en
              «Diagnóstico».
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Escribe una anotación de trabajo (no clínica)…"
            className="min-h-[140px] resize-none"
            disabled={isPending}
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isPending || !text.trim()}>
              {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Guardar anotación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
